<template>
  <div>
    <div ref="borderCursor" class="border-cursor"></div>
    <!-- Cursor center point -->
    <div ref="cursorDot" class="cursor-dot"></div>
    <!-- Outer circle for hover effect -->
    <div ref="cursorCircle" class="cursor-circle"></div>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import { ref, onMounted, onUnmounted, watch } from "vue";
import gsap from "gsap";
import { UseCursorEnabled } from "~/states/animation";
const cursorEnabledRef = UseCursorEnabled();
const router = useRoute();

// Refs for cursor elements
const cursorDot = ref<HTMLElement | null>(null);
const cursorCircle = ref<HTMLElement | null>(null);
const borderCursor = ref<HTMLElement | null>(null);

// Event listener references
let mouseMoveHandler: (e: MouseEvent) => void;
let hoverEnterHandler: (e: MouseEvent) => void;
let hoverLeaveHandler: (e: MouseEvent) => void;
let hoverElements: NodeListOf<HTMLElement>;

/**
 * Attaches event listeners for the custom cursor
 *
 * @remarks
 * Adds event listeners for mouse movement and hover effect on specific elements
 * with the class "cursor-hover". The event handlers are defined in this function.
 */
const attachEventListeners = () => {
  const dot = cursorDot.value;
  const circle = cursorCircle.value;
  const border = borderCursor.value;

  if (!dot || !circle || !border) return;

  /**
   * Handles mouse movement event by updating the position of the cursor
   * elements to follow the mouse position.
   *
   * @param {MouseEvent} e - The mouse event.
   * @remarks
   * This is an event handler for the "mousemove" event on the window object.
   */
  //
  mouseMoveHandler = (e) => {
    const { clientX: x, clientY: y } = e;
    gsap.to(dot, { x, y, duration: 0 });
    gsap.to(border, { x, y, duration: 0.2, ease: "power1.out" });
    gsap.to(circle, { x, y, duration: 0.2, ease: "power1.out" });
  };
  window.addEventListener("mousemove", mouseMoveHandler, { passive: true });
  // Add hover effect for specific elements
  hoverElements = document.querySelectorAll("button, a, .cursor-hover");
  /**
   * Handler for mouse enter event on elements with .cursor-hover class
   * - Scales the outer circle to 2x
   * - Adds "active" class to the outer circle
   * - Adds "hide-default-cursor" class to the body
   */
  hoverEnterHandler = () => {
    gsap.to(circle, { scale: 2 });
    circle.classList.add("active");
    document.body.classList.add("hide-default-cursor");
  };
  /**
   * Handler for mouse leave event on elements with .cursor-hover class
   * - Scales the outer circle back to 1x
   * - Removes the "active" class from the outer circle
   * - Removes the "hide-default-cursor" class from the body
   */
  hoverLeaveHandler = () => {
    gsap.to(circle, { scale: 1 });
    circle.classList.remove("active");
    document.body.classList.remove("hide-default-cursor");
  };
  hoverElements.forEach((el) => {
    el.addEventListener("mouseenter", hoverEnterHandler);
    el.addEventListener("mouseleave", hoverLeaveHandler);
  });
};

/**
 * Removes all event listeners and GSAP animations related to the custom cursor.
 *
 * This function is called when the component is unmounted or when the custom cursor
 * is disabled. It ensures that no lingering event listeners or animations remain,
 * preventing potential memory leaks and restoring default cursor styles.
 *
 * - Removes the mousemove event listener for updating the cursor position.
 * - Removes mouseenter and mouseleave event listeners from elements with hover effects.
 * - Stops GSAP animations for the cursorDot and cursorCircle elements.
 * - Restores the default cursor style by removing the "hide-default-cursor" class from the body.
 */

const removeEventListeners = () => {
  if (mouseMoveHandler)
    window.removeEventListener("mousemove", mouseMoveHandler);

  if (hoverElements) {
    hoverElements.forEach((el) => {
      el.removeEventListener("mouseenter", hoverEnterHandler);
      el.removeEventListener("mouseleave", hoverLeaveHandler);
    });
  }

  gsap.killTweensOf(cursorDot.value);
  gsap.killTweensOf(cursorCircle.value);

  document.body.classList.remove("hide-default-cursor");
};

/**
 * Manages the custom cursor's event listeners based on the cursorEnabledRef value.
 *
 * - Attaches event listeners to track mouse position and hover effects when the cursor is enabled.
 * - Removes event listeners and resets cursor styles when the cursor is disabled.
 */

const cursor = () => {
  if (cursorEnabledRef.value) {
    attachEventListeners();
  } else {
    removeEventListeners();
  }
};

watch(cursorEnabledRef, () => {
  cursor();
});
watch(
  () => router.fullPath,
  () => {
    cursor();
  }
);

onMounted(() => {
  cursor();
});

onUnmounted(() => {
  removeEventListeners();
});
</script>

<style>
/* Hide default cursor on hover */
body.hide-default-cursor * {
  cursor: none !important;
}

/* Cursor dot (center point) */
.cursor-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #6c5ce7;
  position: fixed;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 1000;
  transform: translate(-50%, -50%);
  opacity: 0; /* Hidden by default */
  transition: opacity 0.3s ease;
}
.border-cursor {
  border: 1px solid var(--vt-c-purple);
  border-radius: 50%;
  width: 49px;
  height: 49px;
  position: fixed;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 1000;
  opacity: 0; /* Hidden by default */
  transform: translate(-50%, -50%);
}
/* Outer circle for hover effect */

.cursor-circle {
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: rgba(108, 92, 231, 0.2);
  position: fixed;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 999;
  transform: translate(-50%, -50%);
  /* border: 1px solid var(--vt-c-purple); */
  mix-blend-mode: difference;
  clip-path: circle(50%);
  overflow: unset;
  opacity: 0; /* Hidden by default */
}

/* Show custom cursor when hovering */
body.hide-default-cursor .cursor-dot,
body.hide-default-cursor .border-cursor,
body.hide-default-cursor .cursor-circle {
  opacity: 1;
}

.cursor-circle.active {
  background-color: rgba(255, 255, 255, 0.8);
}
</style>
